import { AutoModeOutlined, Forward, ForwardOutlined, Timeline } from '@mui/icons-material';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLikePrompt } from '../../hooks/useLikePrompt';
import { usePinPrompt } from '../../hooks/usePinPrompt';
import { usePromptDrawer } from '../../hooks/usePromptDrawer';
import { findPrompts } from '../../redux/actions/promptActions';
import {
  selectPrompts,
  setLimit,
  setPage,
  setSortDirection,
  setSortOption,
} from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import { LikeContainer } from '../../theme/CustomComponents';
import {
  PromptSortOptions,
  PromptTableSortOptions,
  PromptView,
  SortDirection,
} from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import { formatDate } from '../../util/dateUtils';
import { getPromptOwnerName } from '../../util/promptMetadata';
import { useAuth } from '../Providers/AuthProvider';
import PinContainer from './PinContainer';
import PromptPinOptionsMenu from './PromptPinOptionsMenu';
import PromptRunButton from './PromptRunButton';

interface PromptsTableProps {
  isAIAppStore: boolean;
}

const PromptsTable: React.FC<PromptsTableProps> = ({ isAIAppStore }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { prompts, isLoading, page, limit, totalCount, sortOption, sortDirection } =
    useSelector(selectPrompts);
  const { currentUser } = useAuth();
  const [currentPinPrompt, setCurrentPinPrompt] = useState<Prompt | null>(null);
  const { handleLike, getLikeTooltipText } = useLikePrompt();

  const { openPromptDrawer } = usePromptDrawer({
    promptView: PromptView.ALL_PROMPTS,
  });

  const {
    handlePinMenuSetup,
    handlePinMenuClose,
    getPinTooltipText,
    pinOptionsAnchorEl,
    pinOptionsMenuOpen,
  } = usePinPrompt({ redirectOnPin: !isAIAppStore });

  const sortTableModel = useMemo(() => {
    if (!sortOption || sortOption === PromptSortOptions.DEFAULT) {
      return [];
    }

    return [
      {
        field: sortOption.toLowerCase(),
        sort: sortDirection.toLowerCase() as 'asc' | 'desc',
      },
    ];
  }, [sortOption, sortDirection]);

  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      //this handles the case when the user clears the sorting
      if (!newModel || newModel.length === 0) {
        dispatch(setSortOption(PromptSortOptions.DEFAULT));
        dispatch(setSortDirection(SortDirection.DESC));
        dispatch(findPrompts());
        return;
      }

      const { field, sort } = newModel[0];

      dispatch(setSortOption(field.toUpperCase() as PromptTableSortOptions));

      dispatch(setSortDirection((sort?.toUpperCase() as SortDirection) || SortDirection.DESC));

      dispatch(findPrompts());
    },
    [dispatch]
  );

  const handleOnPinClick = useCallback(
    (e: React.MouseEvent<Element>, prompt: Prompt) => {
      handlePinMenuSetup(e as React.MouseEvent<HTMLElement>, prompt);
      setCurrentPinPrompt(prompt);
    },
    [handlePinMenuSetup]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<Prompt>) => openPromptDrawer(params.row.id),
    [openPromptDrawer]
  );

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      const newPage = model.page + 1;
      const newLimit = model.pageSize;

      // If limit changed, reset page to 1
      if (newLimit !== limit) {
        dispatch(setPage(1));
        dispatch(setLimit(newLimit));
        dispatch(findPrompts());
        return;
      }

      // Otherwise just update page
      dispatch(setPage(newPage));
      dispatch(findPrompts());
    },
    [dispatch, limit]
  );

  const columns: GridColDef<Prompt>[] = useMemo(
    () => [
      {
        field: 'pinned',
        headerName: '',
        width: 95,
        align: 'center',
        headerAlign: 'left',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1,
              width: '100%',
            }}
          >
            <Tooltip
              title={getPinTooltipText(row.pinned || false, row.folderPinned || false)}
              arrow
            >
              <PinContainer
                pinned={row.pinned}
                folderPinned={row.folderPinned}
                onClick={(e) => handleOnPinClick(e, row)}
              />
            </Tooltip>
            <PromptRunButton isAIAppStore={isAIAppStore} prompt={row} size={20} />
            {row.featured && (
              <Tooltip title="Featured Prompt" arrow>
                <AutoModeOutlined sx={{ height: '20px', width: '20px' }} />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        minWidth: 120,
        sortable: totalCount > 0,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.title} arrow>
            <Typography variant="body2" noWrap>
              {row.title}
            </Typography>
          </Tooltip>
        ),
        headerAlign: 'left',
      },
      {
        field: 'description',
        headerName: 'Description',
        minWidth: 180,
        sortable: totalCount > 0,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap>
            {row.description}
          </Typography>
        ),
        headerAlign: 'left',
      },
      {
        field: 'solutionType',
        headerName: 'Solution Type',
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        headerAlign: 'left',
        valueGetter: ({ row }) => row.solutionType || 'Prompt',
        renderCell: ({ value }) => <Typography variant="body2">{value}</Typography>,
      },
      {
        field: 'labels',
        headerName: 'Labels',
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        headerAlign: 'left',
        renderCell: ({ row }) => {
          const visibleLabels = row.labels?.length ? [...row.labels] : [];
          if (!visibleLabels.length) return null;
          const mainLabel = visibleLabels[0];
          const remainingLabels = visibleLabels.slice(1);

          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                width: '100%',
              }}
            >
              <Chip
                label={mainLabel}
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#070154',
                  backgroundColor: '#E8EEF8',
                }}
              />
              {remainingLabels.length > 0 && (
                <Tooltip title={remainingLabels.join(', ')} arrow>
                  <Chip
                    label={`+${remainingLabels.length}`}
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#070154',
                      backgroundColor: '#E8EEF8',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'createdby',
        headerName: 'Owner',
        minWidth: 140,
        headerAlign: 'left',
        sortable: totalCount > 0,
        disableColumnMenu: true,
        valueGetter: ({ row }) =>
          currentUser?.id === row.ownerId ? 'Me' : getPromptOwnerName(row),
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '1.2',
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: 'createdat',
        headerName: 'Created At',
        minWidth: 170,
        headerAlign: 'left',
        sortable: totalCount > 0,
        disableColumnMenu: true,
        valueGetter: ({ row }) =>
          `${row.parentCreatedAt ? formatDate(row.parentCreatedAt) : 'Unknown Date'}`,
        renderCell: ({ value }) => <Typography variant="body2">{value}</Typography>,
      },
      {
        field: 'updatedat',
        headerName: 'Modified At',
        minWidth: 170,
        headerAlign: 'left',
        sortable: totalCount > 0,
        disableColumnMenu: true,
        valueGetter: ({ row }) => `${row.createdAt ? formatDate(row.createdAt) : 'Unknown Date'}`,
        renderCell: ({ value }) => <Typography variant="body2">{value}</Typography>,
      },
      {
        field: 'usage',
        headerName: 'Usage',
        minWidth: 100,
        headerAlign: 'left',
        sortable: totalCount > 0,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Tooltip title={`${isAIAppStore ? 'App' : 'Prompt'} Usage`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Timeline sx={{ color: 'gray', fontSize: '14px' }} />
              <Typography variant="caption" sx={{ color: 'gray' }}>
                {row.usageCount || 0}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'likes',
        headerName: 'Likes',
        minWidth: 100,
        headerAlign: 'left',
        sortable: totalCount > 0,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Tooltip title={getLikeTooltipText(row.liked || false, isAIAppStore)} arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleLike(row);
              }}
            >
              <LikeContainer
                aria-labelledby={`like-label-${row.id}`}
                liked={row.liked || false}
                style={{ transform: 'rotate(-90deg)' }}
              >
                {row.liked ? (
                  <Forward sx={{ color: '#014361', fontSize: '14px' }} />
                ) : (
                  <ForwardOutlined sx={{ color: 'gray', fontSize: '14px' }} />
                )}
              </LikeContainer>
              <Typography variant="caption" sx={{ color: 'gray' }}>
                {row.likedCount || 0}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
    ],
    [
      currentUser,
      handleOnPinClick,
      handleLike,
      getLikeTooltipText,
      getPinTooltipText,
      totalCount,
      isAIAppStore,
    ]
  );

  return (
    <>
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={prompts}
          columns={columns}
          loading={isLoading}
          rowCount={totalCount || 0}
          getRowHeight={() => 80}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortTableModel}
          onSortModelChange={(newModel) => handleSortModelChange(newModel)}
          onRowClick={handleRowClick}
          onPaginationModelChange={handlePaginationChange}
          paginationModel={{ page: page - 1, pageSize: limit }}
          pagination
          disableRowSelectionOnClick
          disableColumnSelector
          filterDebounceMs={300}
          autoHeight
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell': {
              padding: '12px 10px',
            },
          }}
        />
      </Box>

      {currentPinPrompt && (
        <PromptPinOptionsMenu
          prompt={currentPinPrompt}
          anchorEl={pinOptionsAnchorEl}
          open={pinOptionsMenuOpen}
          onClose={handlePinMenuClose}
          isAIAppStore={isAIAppStore}
        />
      )}
    </>
  );
};

export default PromptsTable;
